import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import Select from 'react-select';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { formatToNewDataStructure } from './EstFunctions';
import UserContext from '../../Context/UserContext';

const PressSheetEstimator = ({ quoteInfo, setQuoteInfo, presses }) => {
  const { papers,customers,employees } = useContext(UserContext);   // only take what you need
  const [activeIndex, setActiveIndex] = useState(0);
  const [forms, setForms] = useState([]);       // we'll manage everything in local state

  const methods = ['Work & Turn', 'Work & Tumble', 'One-Sided', 'Sheetwise', 'Perfect'];
  const { quote } = useParams();

  // Fetch data once when quote changes
  useEffect(() => {
    const getrfq = async () => {
      try {
        // Internal DB first
        const internalRes = await axios.post(
          "http://localhost:5001/rfq/getrfq",
          { quote },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (internalRes.data?.length > 0) {
          const data = internalRes.data[0];
          setQuoteInfo(data);           // keep parent in sync if needed
          setForms(data.forms || []);
          return;
        }

        // Legacy fallback
        const legacyRes = await axios.post(
          "https://highgroundsoftware.com/dash/dashboard/php/getrfqquote.php",
          { quote: quote.replace(/\D/g, '').trim() },
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (legacyRes.data) {
          const formatted = await formatToNewDataStructure(
            legacyRes.data,
            customers, employees, papers   // pass only what formatToNewDataStructure actually needs
          );
          setForms(formatted.forms || []);
          setQuoteInfo(prev => ({ ...prev, forms: formatted.forms || [] })); // optional
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (quote) {
      getrfq();
    }
  }, [quote, papers]);   // ← Important: removed quoteInfo from deps

  // Update a single field immutably
  const updateForm = useCallback((formIndex, field, value) => {
    setForms(prevForms => {
      if (!prevForms || !prevForms[formIndex]) return prevForms;

      const newForms = [...prevForms];
      newForms[formIndex] = {
        ...newForms[formIndex],
        [field]: value
      };

      // Optional: also update parent quoteInfo
      setQuoteInfo(prevQuote => prevQuote ? { ...prevQuote, forms: newForms } : prevQuote);

      return newForms;
    });
  }, [setQuoteInfo]);

  const updateFormSelect = useCallback((formIndex, field, selectedOption) => {
    const value = selectedOption
      ? (field === 'paper' ? selectedOption.paper : selectedOption.pressData || selectedOption.value)
      : null;

    updateForm(formIndex, field, value);
  }, [updateForm]);

  const paperOptions = papers?.map(p => ({
    value: p.stockid,
    label: p.name,
    paper: p
  })) || [];

  const pressOptions = presses?.map(p => ({
    value: p.id || p.name || p.pressId,
    label: p.name || p.description || 'Unnamed Press',
    pressData: p
  })) || [];

  // Show loading / empty state
  if (!forms?.length) {
    return (
      <div className="mx-auto p-6 bg-white rounded-2xl shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-teal-700">Press Sheet Estimator</h2>
          <button className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl font-medium transition">
            + Add New Form
          </button>
        </div>
        <div className="text-center py-12 text-gray-500">
          No forms loaded. Click "Add New Form" to start.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto p-6 bg-white rounded-2xl shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-teal-700">Press Sheet Estimator</h2>
        <button className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl font-medium transition">
          + Add New Form
        </button>
      </div>

      <Tabs selectedIndex={activeIndex} onSelect={setActiveIndex}>
        <TabList className="flex border-b border-gray-200 mb-6 overflow-x-auto" style={{ borderBottom: '1px solid #0F766E' }}>
          {forms.map((form, idx) => (
            <Tab
              key={form.id || `form-${idx}`}
              className="px-6 py-3 text-lg font-medium cursor-pointer border-b-4 border-transparent hover:border-teal-300 data-[selected]:border-teal-600 data-[selected]:text-teal-700 outline-none"
            >
              {form.componentName || `Form ${idx + 1}`}
            </Tab>
          ))}
        </TabList>

        {forms.map((form, idx) => (
          <TabPanel key={form.id || `form-${idx}`}>
            <div className="bg-gray-50 rounded-2xl p-6">
              {/* All your form fields go here – same as before but using `forms` and `updateForm` */}

              {/* Example for Form Name */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button className="px-5 py-3 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-xl font-medium transition flex items-center gap-2 whitespace-nowrap">
                  📋 Duplicate
                </button>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form Name</label>
                  <input
                    type="text"
                    value={form.componentName || ''}
                    placeholder="Enter form name"
                    onChange={(e) => updateForm(idx, 'componentName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                {/* Paper Select - same pattern for all other fields */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paper / Stock</label>
                  <Select
                    options={paperOptions}
                    value={form.paper?.stockid ? paperOptions.find(opt => opt.value === form.paper.stockid) : null}
                    onChange={(option) => updateFormSelect(idx, 'paper', option)}
                    placeholder="Select paper..."
                    isClearable
                  />
                </div>
              </div>

              {/* Add the rest of your fields (Press, Method, Front/Back Inks) using the same updateForm / updateFormSelect pattern */}

              {forms.length > 1 && (
                <button className="mt-10 text-red-600 hover:text-red-700 font-medium">
                  🗑️ Delete this form
                </button>
              )}
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

export default PressSheetEstimator;